import React from 'react'
import { observer } from 'mobx-react-lite'
import { Box, FormControlLabel } from '@material-ui/core'

import { PropFilter } from '../../../components/PropFilter'
import { useStore } from '../../../hooks/storeHook'
import { SyncStore } from '../../../store/syncStore'

import { IntegrationType } from '../../../server/mpsklad_core/Entity/IntegrationType'
import { AccountModelBase } from '../../../server/mpsklad_core/Models/AccountModelBase'

export type PresenceFilterTable = {
  setPropFilter: (key: string, filter: string | undefined) => void

  findPropFilterValue: (key: string) => string | undefined

  getPropFilterReadOnly: (key: string) => boolean
}

export const isPresenceFilterTable = (value: object): value is PresenceFilterTable =>
  'setPropFilter' in value
  && 'findPropFilterValue' in value
  && 'getPropFilterReadOnly' in value

export const otherMarketplaces = (current: IntegrationType): IntegrationType[] => {
  switch (current) {
    case IntegrationType.Ozon:
      return [IntegrationType.Wildberries, IntegrationType.YandexMarket, IntegrationType.MoySklad]

    case IntegrationType.Wildberries:
      return [IntegrationType.Ozon, IntegrationType.YandexMarket, IntegrationType.MoySklad]

    case IntegrationType.YandexMarket:
      return [IntegrationType.Ozon, IntegrationType.Wildberries, IntegrationType.MoySklad]

    case IntegrationType.MoySklad:
      return [IntegrationType.Ozon, IntegrationType.Wildberries, IntegrationType.YandexMarket]

    default: {
      const unknownMarketplace: never = current
      return unknownMarketplace
    }
  }
}

export const presenceFilterKey = (marketplace: IntegrationType): string => {
  switch (marketplace) {
    case IntegrationType.Ozon:
      return 'onOzon'

    case IntegrationType.Wildberries:
      return 'onWb'

    case IntegrationType.YandexMarket:
      return 'onYandexMarket'

    case IntegrationType.MoySklad:
      return 'onMoySklad'

    default: {
      const unknownMarketplace: never = marketplace
      return unknownMarketplace
    }
  }
}

const presenceLabel = (marketplace: IntegrationType): string => {
  switch (marketplace) {
    case IntegrationType.Ozon:
      return 'Есть на Ozon'

    case IntegrationType.Wildberries:
      return 'Есть на Wildberries'

    case IntegrationType.YandexMarket:
      return 'Есть на Яндекс Маркете'

    case IntegrationType.MoySklad:
      return 'Есть в МоемСкладе'

    default: {
      const unknownMarketplace: never = marketplace
      return unknownMarketplace
    }
  }
}

const hasMarketplaceAccount = (marketplace: IntegrationType, syncStore: SyncStore): boolean => {
  switch (marketplace) {
    case IntegrationType.Ozon:
      return syncStore.ozonAccounts.length > 0

    case IntegrationType.Wildberries:
      return syncStore.wbAccounts.length > 0

    case IntegrationType.YandexMarket:
      return syncStore.yandexMarketAccounts.length > 0

    case IntegrationType.MoySklad:
      return syncStore.moySkladAccount != null

    default: {
      const unknownMarketplace: never = marketplace
      return unknownMarketplace
    }
  }
}

export const clearMarketplacePresenceFilters = (
  integrationType: IntegrationType, products: PresenceFilterTable) => {
  for (const marketplace of otherMarketplaces(integrationType)) {
    products.setPropFilter(presenceFilterKey(marketplace), undefined)
  }
}

type MarketplacePresenceFilterProps = {
  account: AccountModelBase

  products: PresenceFilterTable
}

export const MarketplacePresenceFilter =
  observer(({account, products}: MarketplacePresenceFilterProps) => {
    const {syncStore} = useStore()

    const marketplaces = otherMarketplaces(account.integrationType)
      .filter(marketplace => hasMarketplaceAccount(marketplace, syncStore))

    if (marketplaces.length === 0) {
      return null
    }

    return (
      <Box className="filter_wrapper default-border" display="flex" flexWrap="wrap" alignItems="center">
        {
          marketplaces.map(marketplace => {
            const key = presenceFilterKey(marketplace)

            return (
              <FormControlLabel
                key={key}
                labelPlacement="start"
                style={{marginLeft: 16, marginRight: 16}}
                label={<span style={{fontSize: '12px'}}>{presenceLabel(marketplace)}</span>}
                control={
                  <Box marginLeft={1} minWidth={110}>
                    <PropFilter
                      filter={{
                        isBoolean: true,
                        placeholder: 'Все',
                        onText: 'да',
                        offText: 'нет',
                        value: products.findPropFilterValue(key),
                        setValue: value => products.setPropFilter(key, value),
                        readOnly: products.getPropFilterReadOnly(key)
                      }}
                    />
                  </Box>
                }
              />
            )
          })
        }
      </Box>
    )
  })
